import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getAnalytics, getPopularItems } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { StatCard } from "@/components/stat-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eye, TrendingUp, BarChart3, Lock, Package } from "lucide-react";

export function AnalyticsPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const { t } = useI18n();

  const {
    data: analytics,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["analytics", venueId],
    queryFn: () => getAnalytics(venueId!),
    enabled: !!venueId,
    retry: false,
  });

  const { data: popular } = useQuery({
    queryKey: ["analytics", "popular", venueId],
    queryFn: () => getPopularItems(venueId!),
    enabled: !!venueId && !isError,
    retry: false,
  });

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="flex size-16 items-center justify-center rounded-2xl bg-muted mb-4">
          <Lock className="size-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-bold mb-2">{t("pro_required")}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {t("pro_required_desc")}
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold">{t("analytics")}</h1>

      <div className="grid gap-4 sm:grid-cols-3 mb-8">
        <StatCard title={t("total_views_label")} value={analytics?.totalViews ?? 0} icon={Eye} />
        <StatCard title={t("today")} value={analytics?.todayViews ?? 0} icon={TrendingUp} />
        <StatCard title={t("period_label")} value={`${analytics?.period ?? 0} ${t("days_suffix")}`} icon={BarChart3} />
      </div>

      {/* Views by Day */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">{t("daily_views")}</CardTitle>
        </CardHeader>
        <CardContent>
          {analytics?.viewsByDay.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">{t("no_data")}</p>
          ) : (
            <div className="space-y-2">
              {analytics?.viewsByDay.slice(0, 14).map((day) => (
                <div key={day.date} className="flex items-center gap-3">
                  <span className="w-24 text-sm text-muted-foreground">
                    {new Date(day.date).toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                  <div className="flex-1">
                    <div
                      className="h-6 rounded bg-primary/20"
                      style={{
                        width: `${Math.max(
                          4,
                          (day.views / Math.max(...analytics.viewsByDay.map((d) => d.views))) * 100
                        )}%`,
                      }}
                    >
                      <div
                        className="h-full rounded bg-primary"
                        style={{ width: "100%" }}
                      />
                    </div>
                  </div>
                  <span className="w-12 text-right text-sm font-medium">{day.views}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Popular Items */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("popular_items")}</CardTitle>
        </CardHeader>
        <CardContent>
          {!popular || popular.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">{t("no_data")}</p>
          ) : (
            <div className="space-y-3">
              {popular.map((item, i) => (
                <div key={item.id} className="flex items-center gap-3">
                  <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                    {i + 1}
                  </span>
                  {item.image ? (
                    <img src={item.image} alt="" className="size-10 rounded-lg object-cover" />
                  ) : (
                    <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                      <Package className="size-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-muted-foreground">{Number(item.price).toFixed(2)} TRY</p>
                  </div>
                  <Badge variant="secondary">{item.views} {t("views_count")}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
